import { SendEventOnClick, SendEventOnView } from "~/components/Analytics"
import { useId } from "react"
import type { ImageWidget } from "~/types/widgets"
import type { SelectPromotionParams } from "@decocms/apps-commerce/types"
import { Picture, Source } from "~/components/ui/Picture"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"

/**
 * @titleBy href
 */
export interface Banner {
    /**
     * @title Imagem mobile
     */
    srcMobile: ImageWidget
    /**
     * @title Imagem desktop
     */
    srcDesktop?: ImageWidget
    /**
     * @title Texto alternativo
     */
    alt: string
    /**
     * @title Link
     */
    href: string

    /**
     * @title Parâmetros do GA4
     */
    ga4?: SelectPromotionParams
}

export type BorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'

export interface Props {
    /**
     * @description Título
     */
    title?: string
    /**
     * @description Itens por linha
     */
    itemsPerLine: {
        /** @default 2 */
        mobile?: 1 | 2
        /** @default 4 */
        desktop?: 1 | 2 | 4 | 6 | 8
    }
    /**
     * @title Raio da borda
     * @description Raio de borda do item em px
     */
    borderRadius: {
        /** @default none */
        mobile?: BorderRadius
        /** @default none */
        desktop?: BorderRadius
    }
    banners: Banner[]
}

const MOBILE_COLUMNS = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
}

const DESKTOP_COLUMNS = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    4: 'sm:grid-cols-4',
    6: 'sm:grid-cols-6',
    8: 'sm:grid-cols-8',
}

const RADIUS_MOBILE = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full',
}

const RADIUS_DESKTOP = {
    none: 'sm:rounded-none',
    sm: 'sm:rounded-sm',
    md: 'sm:rounded-md',
    lg: 'sm:rounded-lg',
    xl: 'sm:rounded-xl',
    '2xl': 'sm:rounded-2xl',
    '3xl': 'sm:rounded-3xl',
    full: 'sm:rounded-full',
}

const DEFAULT_PROPS: Props = {
    title: 'Summer bags',
    banners: [
        {
            alt: 'a',
            href: 'a',
            srcMobile:
                'https://decoims.com/miess-01/b70e198d-8774-4b72-b510-7c3c6f2f77ff/82727553-f670-4e7c-b9c2-9452aed1955f.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/a3416d34-4236-4ac9-bd21-21958b751ba5/7b3a9d75-57a5-43cf-a3c5-f689a997f24e.jpg',
        },
        {
            alt: 'a',
            href: 'a',
            srcMobile:
                'https://decoims.com/miess-01/96bf6bac-e103-4299-ae19-5e6fa2d07398/c5c6bdf6-5555-488c-8b14-719e4158dea6.jpg',
            srcDesktop:
                'https://decoims.com/miess-01/aab9846b-a715-456a-93e9-6dbedbb50efb/3e2b7824-d75c-4704-8d32-621bfc9b20cf.jpg',
        },
    ],
    borderRadius: {
        mobile: '3xl',
        desktop: '3xl',
    },
    itemsPerLine: {
        mobile: 2,
        desktop: 2,
    },
}

export default function BannnerGrid(props: Props) {
    const { title, itemsPerLine, borderRadius, banners = [] } = { ...DEFAULT_PROPS, ...props }

    return (
        <section className='container w-full px-4 md:px-0 mx-auto'>
            {title && (
                <div className='py-6 md:py-0 md:pb-[40px] flex items-center mt-6'>
                    <h2 className='text-lg leading-5 font-semibold uppercase'>{title}</h2>

                    <div className='bg-[#e5e5ea] h-[1px] w-full ml-4'></div>
                </div>
            )}
            <div
                className={`grid gap-4 md:gap-6 ${MOBILE_COLUMNS[itemsPerLine?.mobile ?? 2]} ${
                    DESKTOP_COLUMNS[itemsPerLine?.desktop ?? 4]
                }`}
            >
                {banners.map(({ href, srcMobile, srcDesktop, alt, ga4 }) => {
                    const id = useId()

                    const analyticsParams = {
                        promotion_id: ga4?.promotion_id ?? 'banner_grid',
                        promotion_name: ga4?.promotion_name ?? alt ?? 'Banner Grid',
                        creative_name: ga4?.creative_name,
                        creative_slot: ga4?.creative_slot,
                        view: {
                            id: ga4?.promotion_id ?? 'banner_grid',
                            name: ga4?.promotion_name ?? alt ?? 'Banner Grid',
                        },
                    }

                    const viewPromotionEvent = toAnalytics({
                        type: 'view_promotion',
                        data: analyticsParams,
                    })

                    const selectPromotionEvent = toAnalytics({
                        type: 'select_promotion',
                        data: analyticsParams,
                    })

                    return (
                        <a
                            id={id}
                            href={href}
                            className={`overflow-hidden ${
                                RADIUS_MOBILE[borderRadius.mobile ?? 'none']
                            } ${RADIUS_DESKTOP[borderRadius.desktop ?? 'none']} `}
                        >
                            <Picture>
                                <Source
                                    media='(max-width: 767px)'
                                    src={srcMobile}
                                    width={100}
                                    height={100}
                                />
                                <Source
                                    media='(min-width: 768px)'
                                    src={srcDesktop ? srcDesktop : srcMobile}
                                    width={250}
                                    height={250}
                                />
                                <img
                                    className='w-full'
                                    sizes='(max-width: 640px) 100vw, 30vw'
                                    src={srcMobile}
                                    alt={alt}
                                    decoding='async'
                                    loading='lazy'
                                />
                            </Picture>

                            <SendEventOnView id={id} event={viewPromotionEvent} />
                            <SendEventOnClick id={id} event={selectPromotionEvent} />
                        </a>
                    )
                })}
            </div>
        </section>
    )
}
